import { IReader, observableValue } from '@vscode/observables';
import type { ScreenshotReport, ReportFixtureEntry } from './ScreenshotReport.js';
import type { ExplorerTreeNode } from '../components/types.js';
import { FixturePath } from '../core/FixturePath.js';

type ReportState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly report: ScreenshotReport }
  | { readonly status: 'error'; readonly message: string };

export class ReportModel {
  readonly state = observableValue<ReportState>(this, { status: 'loading' });

  private readonly _baseUrl: string;

  constructor(readonly reportUrl: string) {
    // Base URL = everything up to and including the last '/'
    const lastSlash = reportUrl.lastIndexOf('/');
    this._baseUrl = lastSlash >= 0 ? reportUrl.slice(0, lastSlash + 1) : '';
    this._fetch();
  }

  resolveScreenshotUrl(relativePath: string): string {
    return this._baseUrl + relativePath;
  }

  buildTree(reader: IReader): ExplorerTreeNode {
    const state = this.state.read(reader);
    if (state.status !== 'ready') {
      return { id: new FixturePath(''), name: 'Report', type: 'folder', children: [], labels: [] };
    }

    return buildTreeFromFixtureIds(state.report.fixtures);
  }

  private async _fetch(): Promise<void> {
    this.state.set({ status: 'loading' }, undefined);
    try {
      const response = await fetch(this.reportUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const report: ScreenshotReport = await response.json();
      this.state.set({ status: 'ready', report }, undefined);
    } catch (e) {
      this.state.set({ status: 'error', message: String(e) }, undefined);
    }
  }
}

interface MutableTreeNode {
  id: FixturePath;
  name: string;
  type: 'folder' | 'component';
  children?: MutableTreeNode[];
  reportEntry?: ReportFixtureEntry;
  labels: string[];
}

function buildTreeFromFixtureIds(fixtures: { readonly [fixtureId: string]: ReportFixtureEntry | undefined }): ExplorerTreeNode {
  const root: MutableTreeNode = { id: new FixturePath(''), name: 'Components', type: 'folder', children: [], labels: [] };

  for (const [fixtureId, entry] of Object.entries(fixtures)) {
    const segments = new FixturePath(fixtureId).segments;
    let current = root;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const isLeaf = i === segments.length - 1;
      const childId = new FixturePath(segments.slice(0, i + 1).join('/'));

      if (isLeaf) {
        const labels = [...(entry?.labels ?? [])];
        if (entry?.status === 'changed' && !labels.includes('changed')) {
          labels.push('changed');
        }
        current.children!.push({
          id: childId,
          name: segment,
          type: 'component',
          reportEntry: entry,
          labels,
        });
      } else {
        let folder = current.children!.find(c => c.type === 'folder' && c.name === segment);
        if (!folder) {
          folder = { id: childId, name: segment, type: 'folder', children: [], labels: [] };
          current.children!.push(folder);
        }
        current = folder;
      }
    }
  }

  return root;
}
